import axios from 'axios'
import moment from 'moment-timezone'
import queryString, { stringify } from 'query-string'
import React from 'react'
import { matchPath } from 'react-router'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ExtendAnalyticsBrowser } from 'src/analytics/AnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  ApproveBudgetSuccessToastMessage,
  CancelBudgetSuccessToastMessage,
  CloseAccountToastMessage,
  CreateBudgetToastMessage,
  CreateCreditCardNeedsValidationToastMessage,
  CreateCreditCardNeedsVerificationToastMessage,
  CreateCreditCardVerificationSuccessMessage,
  DeactivateBudgetFailureToastMessage,
  DeactivateBudgetToastMessage,
  NewVerificationCodeSentToastMessage,
  RejectBudgetSuccessToastMessage,
  RemoveUserFromBudgetToastMessage,
  RequestBudgetToastMessage,
  UpdateBudgetSuccessToastMessage,
  UpdateBudgetToastMessage,
} from 'src/app/Toast/Toast'
import { IssuerCodes } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { translateSingleString } from 'src/localization/Trns'
import { BinRangeResponse } from 'src/models/binRange.models'
import {
  BudgetRequestListResponse,
  BudgetRequestStatus,
} from 'src/models/budgetRequest.models'
import {
  CardProperties,
  CreditCardDisplayStatus,
  CreditCardListModel,
  CreditCardListResponse,
  CreditCardStatistics,
  CreditCardStatus,
  CreditCardType,
} from 'src/models/creditCard.models'
import { CreditCardPermissionsResponse } from 'src/models/creditCardPermissions.models'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  TransactionStatus,
  TransactionType,
} from 'src/models/transaction.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { closeModal, connectedPush } from 'src/navigation/Lnk'
import {
  accountOverviewUrl,
  budgetOverviewUrl,
  homeUrl,
  PRICING_ROUTE,
  registerAccountUrl,
} from 'src/navigation/urls'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'
import { queryClient } from 'src/queryClient'
import { createSetIsRemoveBudgetUserInvitePopupOpen } from 'src/redux/actions/app.actions'
import { getIssuer } from 'src/redux/actions/data/issuerIntegrators.actions'
import { LocationState, RootState } from 'src/redux/createRootReducer'
import { selectStatisticsDispatcherFromState } from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectAuthedJsonApiRequestConfig,
  selectAuthedJsonVaultRequestConfig,
} from 'src/redux/selectors/axios.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { GetCreditCardsRequest } from 'src/requests/getCreditCards.request'
import { PatchAccountRequest } from 'src/requests/patchAccount.request'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'
import { PutCreditCardRequest } from 'src/requests/putCreditCard.request'
import {
  StatisticAggregateType,
  StatisticModelType,
  TransactionStatisticQueryFieldType,
} from 'src/requests/statistics.request'
import { BackendErrorPayload } from 'src/utils/error.utils'

const cacheTimestamp = moment().format('dddd, Do MMM YYYY, h:mm:ss A')

export const GET_CREDIT_CARD_PERMISSIONS_INIT =
  'data/creditCards/GET_CREDIT_CARD_PERMISSIONS_INIT'
export const GET_CREDIT_CARD_PERMISSIONS_SUCCESS =
  'data/creditCards/GET_CREDIT_CARD_PERMISSIONS_SUCCESS'
export const GET_CREDIT_CARD_PERMISSIONS_FAILURE =
  'data/creditCards/GET_CREDIT_CARD_PERMISSIONS_FAILURE'

export type GetCreditCardPermissionsSuccessPayload =
  CreditCardPermissionsResponse & {
    creditCardId: string
  }

export interface GetCreditCardPermissionsInitAction {
  type: typeof GET_CREDIT_CARD_PERMISSIONS_INIT
}
export interface GetCreditCardPermissionsSuccessAction {
  type: typeof GET_CREDIT_CARD_PERMISSIONS_SUCCESS
  payload: GetCreditCardPermissionsSuccessPayload
}
export interface GetCreditCardPermissionsFailureAction {
  type: typeof GET_CREDIT_CARD_PERMISSIONS_FAILURE
  payload: unknown
}

export type GetCreditCardPermissionsActions =
  | GetCreditCardPermissionsInitAction
  | GetCreditCardPermissionsSuccessAction
  | GetCreditCardPermissionsFailureAction

const createGetCreditCardPermissionsInitAction =
  (): GetCreditCardPermissionsInitAction => ({
    type: GET_CREDIT_CARD_PERMISSIONS_INIT,
  })
const createGetCreditCardPermissionsSuccessAction = (
  payload: GetCreditCardPermissionsSuccessPayload,
): GetCreditCardPermissionsSuccessAction => ({
  type: GET_CREDIT_CARD_PERMISSIONS_SUCCESS,
  payload,
})
const createGetCreditCardPermissionsFailureAction = (
  response: unknown,
): GetCreditCardPermissionsFailureAction => ({
  type: GET_CREDIT_CARD_PERMISSIONS_FAILURE,
  payload: response,
})

export const getCreditCardPermissions = (
  creditCardId: string,
  parentCreditCardId?: string,
): ThunkAction<
  Promise<
    | GetCreditCardPermissionsFailureAction
    | GetCreditCardPermissionsSuccessAction
    | undefined
  >,
  RootState,
  void,
  GetCreditCardPermissionsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetCreditCardPermissionsActions>,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardPermissionsFailureAction
    | GetCreditCardPermissionsSuccessAction
    | undefined
  > => {
    dispatch(createGetCreditCardPermissionsInitAction())
    return axios
      .get<GetCreditCardPermissionsSuccessPayload>(
        `/creditcards/${creditCardId}/permissions`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardPermissionsSuccessAction({
              ...response.data,
              creditCardId,
            }),
          )
        },
        (err) => {
          if (parentCreditCardId !== undefined) {
            // should there be an action returned here?
            dispatch(getCreditCardPermissions(parentCreditCardId))
          } else {
            return dispatch(
              createGetCreditCardPermissionsFailureAction(err.response.data),
            )
          }
        },
      )
  }
}

export const PUT_CREDIT_CARD_INIT = 'data/creditCards/PUT_CREDIT_CARD_INIT'
export const PUT_CREDIT_CARD_ACCOUNT_INIT =
  'data/creditCards/PUT_CREDIT_CARD_ACCOUNT_INIT'
export const PUT_CREDIT_CARD_BILLING_INIT =
  'data/creditCards/PUT_CREDIT_CARD_BILLING_INIT'
export const PUT_CREDIT_CARD_SUCCESS =
  'data/creditCards/PUT_CREDIT_CARD_SUCCESS'
export const PUT_CREDIT_CARD_FAILURE =
  'data/creditCards/PUT_CREDIT_CARD_FAILURE'

export type PutCreditCardSuccessPayload = {
  creditCard: CreditCardListResponse
}

export type PutCreditCardFailurePayload = BackendErrorPayload<string>

export interface PutCreditCardInitAction {
  type: typeof PUT_CREDIT_CARD_INIT
}
export interface PutCreditCardAccountInitAction {
  type: typeof PUT_CREDIT_CARD_ACCOUNT_INIT
}
export interface PutCreditCardBillingInitAction {
  type: typeof PUT_CREDIT_CARD_BILLING_INIT
}
export interface PutCreditCardSuccessAction {
  type: typeof PUT_CREDIT_CARD_SUCCESS
  payload: PutCreditCardSuccessPayload
}
export interface PutCreditCardFailureAction {
  type: typeof PUT_CREDIT_CARD_FAILURE
  payload: PutCreditCardFailurePayload
}

export type PutCreditCardActions =
  | PutCreditCardInitAction
  | PutCreditCardAccountInitAction
  | PutCreditCardBillingInitAction
  | PutCreditCardSuccessAction
  | PutCreditCardFailureAction

const createPutCreditCardInitAction = (): PutCreditCardInitAction => ({
  type: PUT_CREDIT_CARD_INIT,
})
const createPutCreditCardAccountInitAction =
  (): PutCreditCardAccountInitAction => ({
    type: PUT_CREDIT_CARD_ACCOUNT_INIT,
  })
const createPutCreditCardBillingInitAction =
  (): PutCreditCardBillingInitAction => ({
    type: PUT_CREDIT_CARD_BILLING_INIT,
  })
const createPutCreditCardSuccessAction = (
  payload: PutCreditCardSuccessPayload,
): PutCreditCardSuccessAction => ({
  type: PUT_CREDIT_CARD_SUCCESS,
  payload,
})
const createPutCreditCardFailureAction = (
  payload: PutCreditCardFailurePayload,
): PutCreditCardFailureAction => ({
  type: PUT_CREDIT_CARD_FAILURE,
  payload,
})

// TODO: switch the message sent based on targets? or switch in the creator
export const putCreditCard = (
  id: string,
  body: Partial<PutCreditCardRequest>,
  shouldCloseModalAndOpenToast?: boolean,
  action?: PutCreditCardActions,
): ThunkAction<Promise<void>, RootState, void, PutCreditCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutCreditCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    switch (action?.type) {
      case PUT_CREDIT_CARD_ACCOUNT_INIT:
        dispatch(createPutCreditCardAccountInitAction())
        break
      case PUT_CREDIT_CARD_BILLING_INIT:
        dispatch(createPutCreditCardBillingInitAction())
        break
      default:
        dispatch(createPutCreditCardInitAction())
    }
    return axios
      .put<PutCreditCardSuccessPayload>(
        `/creditcards/${id}`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutCreditCardSuccessAction(response.data))
          dispatch(
            getCreditCardsStatisticsActiveBudgetCount(
              [response.data.creditCard.id],
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsBudgetCount(
              [response.data.creditCard.id],
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsActiveVcnCount([
              response.data.creditCard.id,
            ]),
          )
          dispatch(
            getCreditCardsStatisticsVcnCount([response.data.creditCard.id]),
          )
          if (shouldCloseModalAndOpenToast) {
            dispatch(closeModal())
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <UpdateBudgetToastMessage
                    creditCard={
                      new CreditCardListModel(response.data.creditCard)
                    }
                    users={selectUserListModelsFromState(getState())}
                  />
                ),
              }),
            )
          }
        },
        (err) => {
          dispatch(createPutCreditCardFailureAction(err.response.data))
        },
      )
  }
}

export const updateBudgetUsers = (
  id: string,
  emails: string[],
  shouldCloseModalAndOpenToast?: boolean,
): ThunkAction<Promise<void>, RootState, void, PostCreditCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostCreditCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    return axios
      .post<PostCreditCardSuccessPayload>(
        `/creditcards/${id}/users`,
        { emails: emails },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostCreditCardSuccessAction(response.data))
          if (shouldCloseModalAndOpenToast) {
            dispatch(closeModal())
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <UpdateBudgetToastMessage
                    creditCard={
                      new CreditCardListModel(response.data.creditCard)
                    }
                    users={selectUserListModelsFromState(getState())}
                  />
                ),
              }),
            )
          }
        },
        (err) => {
          dispatch(createPostCreditCardFailureAction(err.response.data))
        },
      )
  }
}

export const DELETE_BUDGET_USERS_INIT =
  'data/creditCards/DELETE_BUDGET_USERS_INIT'
export const DELETE_BUDGET_USERS_SUCCESS =
  'data/creditCards/DELETE_BUDGET_USERS_SUCCESS'
export const DELETE_BUDGET_USERS_FAILURE =
  'data/creditCards/DELETE_BUDGET_USERS_FAILURE'

export type DeleteBudgetUsersSuccessPayload = {
  creditCard: CreditCardListResponse
}
export type DeleteBudgetUsersFailurePayload = BackendErrorPayload<string>

export interface DeleteBudgetUsersInitAction {
  type: typeof DELETE_BUDGET_USERS_INIT
}
export interface DeleteBudgetUsersSuccessAction {
  type: typeof DELETE_BUDGET_USERS_SUCCESS
  payload: DeleteBudgetUsersSuccessPayload
}
export interface DeleteBudgetUsersFailureAction {
  type: typeof DELETE_BUDGET_USERS_FAILURE
  payload: DeleteBudgetUsersFailurePayload
}

export type DeleteBudgetUsersActions =
  | DeleteBudgetUsersInitAction
  | DeleteBudgetUsersSuccessAction
  | DeleteBudgetUsersFailureAction

const createDeleteBudgetUsersInitAction = (): DeleteBudgetUsersInitAction => ({
  type: DELETE_BUDGET_USERS_INIT,
})
const createDeleteBudgetUsersSuccessAction = (
  payload: DeleteBudgetUsersSuccessPayload,
): DeleteBudgetUsersSuccessAction => ({
  type: DELETE_BUDGET_USERS_SUCCESS,
  payload,
})
const createDeleteBudgetUsersFailureAction = (
  payload: DeleteBudgetUsersFailurePayload,
): DeleteBudgetUsersFailureAction => ({
  type: DELETE_BUDGET_USERS_FAILURE,
  payload,
})

export const deleteBudgetUsers = (
  id: string,
  emails: string[],
  firstName: string | undefined,
  lastName: string | undefined,
): ThunkAction<Promise<void>, RootState, void, DeleteBudgetUsersActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeleteBudgetUsersActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createDeleteBudgetUsersInitAction())
    const authedJson = selectAuthedJsonApiRequestConfig(getState())
    return axios
      .delete(`/creditcards/${id}/users`, {
        headers: authedJson.headers,
        baseURL: authedJson.baseURL,
        data: {
          emails: emails,
        },
      })
      .then(
        (response) => {
          dispatch(createDeleteBudgetUsersSuccessAction(response.data))
          dispatch(createSetIsRemoveBudgetUserInvitePopupOpen(false))
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <RemoveUserFromBudgetToastMessage
                  creditCard={new CreditCardListModel(response.data.creditCard)}
                  firstName={firstName}
                  lastName={lastName}
                  email={emails[0]}
                />
              ),
            }),
          )
        },
        (err) => {
          dispatch(createDeleteBudgetUsersFailureAction(err.response.data))
        },
      )
  }
}

export const POST_CREDIT_CARD_INIT = 'data/creditCards/POST_CREDIT_CARD_INIT'
export const POST_CREDIT_CARD_SUCCESS =
  'data/creditCards/POST_CREDIT_CARD_SUCCESS'
export const POST_CREDIT_CARD_FAILURE =
  'data/creditCards/POST_CREDIT_CARD_FAILURE'

export type PostCreditCardSuccessPayload = {
  creditCard: CreditCardListResponse
}

export type PostBudgetRequestSuccessPayload = {
  budgetRequest: BudgetRequestListResponse
}

export type PostCreditCardFailurePayload = BackendErrorPayload<string>

export interface PostCreditCardInitAction {
  type: typeof POST_CREDIT_CARD_INIT
}
export interface PostCreditCardSuccessAction {
  type: typeof POST_CREDIT_CARD_SUCCESS
  payload: PostCreditCardSuccessPayload
}
export interface PostCreditCardFailureAction {
  type: typeof POST_CREDIT_CARD_FAILURE
  payload: PostCreditCardFailurePayload
}

export type PostCreditCardActions =
  | PostCreditCardInitAction
  | PostCreditCardSuccessAction
  | PostCreditCardFailureAction

const createPostCreditCardInitAction = (): PostCreditCardInitAction => ({
  type: POST_CREDIT_CARD_INIT,
})
const createPostCreditCardSuccessAction = (
  payload: PostCreditCardSuccessPayload,
): PostCreditCardSuccessAction => ({
  type: POST_CREDIT_CARD_SUCCESS,
  payload,
})
const createPostCreditCardFailureAction = (
  payload: PostCreditCardFailurePayload,
): PostCreditCardFailureAction => ({
  type: POST_CREDIT_CARD_FAILURE,
  payload,
})

export const postCreditCardAndOpenToastAndCloseFlyout = (
  body: Partial<PostCreditCardRequest>,
): ThunkAction<Promise<void>, RootState, void, PostCreditCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostCreditCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    const dispatchStatistics = selectStatisticsDispatcherFromState(getState())
    dispatch(createPostCreditCardInitAction())
    return axios
      .post<PostCreditCardSuccessPayload>(
        `/creditcards`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostCreditCardSuccessAction(response.data))
          dispatch(closeModal())
          dispatch(
            getCreditCardsStatisticsActiveBudgetCount(
              [response.data.creditCard.id],
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsBudgetCount(
              [response.data.creditCard.id],
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsActiveVcnCount([
              response.data.creditCard.id,
            ]),
          )
          dispatch(
            getCreditCardsStatisticsVcnCount([response.data.creditCard.id]),
          )
          dispatchStatistics([
              appStatisticsKeys.CreditCardsCanIssueVirtualCardsCountStatistics,
              appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
          ], cacheTimestamp)
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <CreateBudgetToastMessage
                  creditCard={new CreditCardListModel(response.data.creditCard)}
                  users={selectUserListModelsFromState(getState())}
                  emails={(body.emails?.length ?? 0) < 2}
                />
              ),
            }),
          )
        },
        (err) => {
          dispatch(createPostCreditCardFailureAction(err.response.data))
        },
      )
  }
}

export const postBudgetRequestAndOpenToastAndCloseFlyout = (
  body: Partial<PostCreditCardRequest>,
): ThunkAction<Promise<void>, RootState, void, PostCreditCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostCreditCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostCreditCardInitAction())
    return axios
      .post<PostBudgetRequestSuccessPayload>(
        `/budgetrequests`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const { budgetRequest } = response.data
          const {
            approver: {
              firstName: approverFirstName,
              lastName: approverLastName,
            },
          } = budgetRequest
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <RequestBudgetToastMessage
                  budgetName={budgetRequest.displayName}
                  user={`${approverFirstName} ${approverLastName}`}
                />
              ),
            }),
          )
          dispatch(closeModal())
        },
        (err) => {
          dispatch(createPostCreditCardFailureAction(err.response.data))
        },
      )
  }
}

export const putBudgetRequestAndOpenToastAndCloseFlyout = (
  body: Partial<PostCreditCardRequest>,
  id: string,
  status: BudgetRequestStatus,
): ThunkAction<Promise<void>, RootState, void, PostCreditCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostCreditCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostCreditCardInitAction())
    return axios
      .patch<PostBudgetRequestSuccessPayload>(
        `/budgetrequests/${id}`,
        { ...body, status },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const { budgetRequest } = response.data

          dispatch(
            createSetIsToastOpen(
              true,
              status === BudgetRequestStatus.Approved
                ? {
                    toastMessage: (
                      <ApproveBudgetSuccessToastMessage
                        budgetName={budgetRequest.displayName}
                      />
                    ),
                  }
                : status === BudgetRequestStatus.Rejected
                  ? {
                      toastMessage: (
                        <RejectBudgetSuccessToastMessage
                          budgetName={budgetRequest.displayName}
                        />
                      ),
                    }
                  : status === BudgetRequestStatus.Cancelled
                    ? {
                        toastMessage: (
                          <CancelBudgetSuccessToastMessage
                            budgetName={budgetRequest.displayName}
                          />
                        ),
                      }
                    : {
                        toastMessage: (
                          <UpdateBudgetSuccessToastMessage
                            budgetName={budgetRequest.displayName}
                          />
                        ),
                      },
            ),
          )
          dispatch(closeModal())
        },
        (err) => {
          dispatch(createPostCreditCardFailureAction(err.response.data))
        },
      )
  }
}

export const postSecureCreditCardAndOpenToastAndCloseFlyout = (
  body: Partial<PostCreditCardRequest>,
  locationState: LocationState,
  isExtendBrand: boolean,
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, PostCreditCardActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostCreditCardActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostCreditCardInitAction())
    return axios
      .post<PostCreditCardSuccessPayload>(
        `/creditcards`,
        body,
        selectAuthedJsonVaultRequestConfig(getState()),
      )
      .then(
        (response) => {
          const calculatedBackgroundLocation =
            selectCalculatedBackgroundLocation(getState())
          const dispatchStatistics =
            selectStatisticsDispatcherFromState(getState())
          matchPath(calculatedBackgroundLocation.pathname, homeUrl()) &&
            analytics?.track(
              'User submitted a request for a budget from Create New Menu',
              {
                type_of_budget: response.data.creditCard.type,
                budget_name: response.data.creditCard.displayName,
                budget_limit: response.data.creditCard.issuingLimitCents,
                activeUntil: response.data.creditCard.activeUntil,
              },
            )
          dispatch(createPostCreditCardSuccessAction(response.data))
          dispatch(closeModal())
          if (response.data.creditCard.lastVerificationRequestedAt) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <CreateCreditCardNeedsVerificationToastMessage />,
              }),
            )
          } else if (
            response.data.creditCard.displayStatus ===
            CreditCardDisplayStatus.Pending
          ) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <CreateCreditCardNeedsValidationToastMessage />,
              }),
            )
          } else if (
            response.data.creditCard.displayStatus ===
            CreditCardDisplayStatus.Active
          ) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <CreateCreditCardVerificationSuccessMessage />,
              }),
            )
          }
          dispatchStatistics([
            appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
          ])
          // TODO: @bjunya - replace hardcoded url with Route.PATH_NAME() function
          if (
            locationState.backgroundLocation?.pathname === '/onboarding' &&
            isExtendBrand
          ) {
            dispatch(connectedPush(PRICING_ROUTE(response.data.creditCard.id)))
            return
          }
          dispatch(
            connectedPush(accountOverviewUrl(response.data.creditCard.id)),
          )
        },
        (err) => {
          dispatch(createPostCreditCardFailureAction(err.response.data))
        },
      )
  }
}

export const GET_CREDIT_CARD_INIT = 'data/creditCards/GET_CREDIT_CARD_INIT'
export const GET_CREDIT_CARD_SUCCESS =
  'data/creditCards/GET_CREDIT_CARD_SUCCESS'
export const GET_CREDIT_CARD_FAILURE =
  'data/creditCards/GET_CREDIT_CARD_FAILURE'

export type GetCreditCardSuccessPayload = {
  creditCard: CreditCardListResponse
}

export interface GetCreditCardInitAction {
  type: typeof GET_CREDIT_CARD_INIT
}
export interface GetCreditCardSuccessAction {
  type: typeof GET_CREDIT_CARD_SUCCESS
  payload: GetCreditCardSuccessPayload
}
export interface GetCreditCardFailureAction {
  type: typeof GET_CREDIT_CARD_FAILURE
  payload: unknown
}

export type GetCreditCardActions =
  | GetCreditCardInitAction
  | GetCreditCardSuccessAction
  | GetCreditCardFailureAction

const createGetCreditCardInitAction = (): GetCreditCardInitAction => ({
  type: GET_CREDIT_CARD_INIT,
})
export const createGetCreditCardSuccessAction = (
  payload: GetCreditCardSuccessPayload,
): GetCreditCardSuccessAction => ({
  type: GET_CREDIT_CARD_SUCCESS,
  payload,
})
const createGetCreditCardFailureAction = (
  response: unknown,
): GetCreditCardFailureAction => ({
  type: GET_CREDIT_CARD_FAILURE,
  payload: response,
})

export const getCreditCard = (
  creditCardId: string,
): ThunkAction<
  Promise<GetCreditCardSuccessAction | GetCreditCardFailureAction>,
  RootState,
  void,
  GetCreditCardActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetCreditCardActions>,
    getState: () => RootState,
  ): Promise<GetCreditCardSuccessAction | GetCreditCardFailureAction> => {
    dispatch(createGetCreditCardInitAction())
    return axios
      .get<GetCreditCardSuccessPayload>(
        `/creditcards/${creditCardId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(
            getCreditCardsStatisticsActiveBudgetCount(
              [response.data.creditCard.id],
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsBudgetCount(
              [response.data.creditCard.id],
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsActiveVcnCount([
              response.data.creditCard.id,
            ]),
          )
          dispatch(
            getCreditCardsStatisticsVcnCount([response.data.creditCard.id]),
          )
          return dispatch(createGetCreditCardSuccessAction(response.data))
        },
        async (err) => {
          return dispatch(createGetCreditCardFailureAction(err.response.data))
        },
      )
  }
}

export const GET_PARENT_CREDIT_CARD_INIT =
  'data/creditCards/GET_PARENT_CREDIT_CARD_INIT'
export const GET_PARENT_CREDIT_CARD_SUCCESS =
  'data/creditCards/GET_PARENT_CREDIT_CARD_SUCCESS'
export const GET_PARENT_CREDIT_CARD_FAILURE =
  'data/creditCards/GET_PARENT_CREDIT_CARD_FAILURE'

export type GetParentCreditCardSuccessPayload = {
  creditCard: CreditCardListResponse
}

export interface GetParentCreditCardInitAction {
  type: typeof GET_PARENT_CREDIT_CARD_INIT
}
export interface GetParentCreditCardSuccessAction {
  type: typeof GET_PARENT_CREDIT_CARD_SUCCESS
  payload: GetCreditCardSuccessPayload
}
export interface GetParentCreditCardFailureAction {
  type: typeof GET_PARENT_CREDIT_CARD_FAILURE
  payload: unknown
}

export type GetParentCreditCardActions =
  | GetParentCreditCardInitAction
  | GetParentCreditCardSuccessAction
  | GetParentCreditCardFailureAction

const createGetParentCreditCardInitAction =
  (): GetParentCreditCardInitAction => ({
    type: GET_PARENT_CREDIT_CARD_INIT,
  })
const createGetParentCreditCardSuccessAction = (
  payload: GetCreditCardSuccessPayload,
): GetParentCreditCardSuccessAction => ({
  type: GET_PARENT_CREDIT_CARD_SUCCESS,
  payload,
})
const createGetParentCreditCardFailureAction = (
  response: unknown,
): GetParentCreditCardFailureAction => ({
  type: GET_PARENT_CREDIT_CARD_FAILURE,
  payload: response,
})

export const getParentCreditCard = (
  creditCardId: string,
): ThunkAction<
  Promise<GetParentCreditCardSuccessAction | GetParentCreditCardFailureAction>,
  RootState,
  void,
  GetParentCreditCardActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetParentCreditCardActions>,
    getState: () => RootState,
  ): Promise<
    GetParentCreditCardSuccessAction | GetParentCreditCardFailureAction
  > => {
    dispatch(createGetParentCreditCardInitAction())
    return axios
      .get<GetCreditCardSuccessPayload>(
        `/creditcards/${creditCardId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(createGetParentCreditCardSuccessAction(response.data))
        },
        async (err) => {
          return dispatch(
            createGetParentCreditCardFailureAction(err.response.data),
          )
        },
      )
  }
}

export const PATCH_CREDIT_CARD_STATUS_INIT =
  'data/creditCards/PATCH_CREDIT_CARD_STATUS_INIT'
export const PATCH_CREDIT_CARD_STATUS_SUCCESS =
  'data/creditCards/PATCH_CREDIT_CARD_STATUS_SUCCESS'
export const PATCH_CREDIT_CARD_STATUS_FAILURE =
  'data/creditCards/PATCH_CREDIT_CARD_STATUS_FAILURE'

export type PatchCreditCardStatusSuccessPayload = {
  creditCard: CreditCardListResponse
}

export interface PatchCreditCardStatusInitAction {
  type: typeof PATCH_CREDIT_CARD_STATUS_INIT
}
export interface PatchCreditCardStatusSuccessAction {
  type: typeof PATCH_CREDIT_CARD_STATUS_SUCCESS
  payload: PatchCreditCardStatusSuccessPayload
}
export interface PatchCreditCardStatusFailureAction {
  type: typeof PATCH_CREDIT_CARD_STATUS_FAILURE
  payload: unknown
}

export type PatchCreditCardStatusActions =
  | PatchCreditCardStatusInitAction
  | PatchCreditCardStatusSuccessAction
  | PatchCreditCardStatusFailureAction

const createPatchCreditCardStatusInitAction =
  (): PatchCreditCardStatusInitAction => ({
    type: PATCH_CREDIT_CARD_STATUS_INIT,
  })
const createPatchCreditCardStatusSuccessAction = (
  payload: PatchCreditCardStatusSuccessPayload,
): PatchCreditCardStatusSuccessAction => ({
  type: PATCH_CREDIT_CARD_STATUS_SUCCESS,
  payload,
})
const createPatchCreditCardStatusFailureAction = (
  response: unknown,
): PatchCreditCardStatusFailureAction => ({
  type: PATCH_CREDIT_CARD_STATUS_FAILURE,
  payload: response,
})

export const patchCreditCardStatus = (
  id: string,
): ThunkAction<
  Promise<
    PatchCreditCardStatusFailureAction | PatchCreditCardStatusSuccessAction
  >,
  RootState,
  void,
  PatchCreditCardStatusActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PatchCreditCardStatusActions>,
    getState: () => RootState,
  ): Promise<
    PatchCreditCardStatusFailureAction | PatchCreditCardStatusSuccessAction
  > => {
    dispatch(createPatchCreditCardStatusInitAction())
    return axios
      .patch<PatchCreditCardStatusSuccessPayload>(
        `/creditcards/${id}/status`,
        {},
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(
            getCreditCardsStatisticsActiveBudgetCount(
              [response.data.creditCard.id],
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsBudgetCount(
              [response.data.creditCard.id],
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsActiveVcnCount([
              response.data.creditCard.id,
            ]),
          )
          dispatch(
            getCreditCardsStatisticsVcnCount([response.data.creditCard.id]),
          )
          return dispatch(
            createPatchCreditCardStatusSuccessAction(response.data),
          )
        },
        (err) => {
          return dispatch(
            createPatchCreditCardStatusFailureAction(err.response.data),
          )
        },
      )
  }
}

export const DEACTIVATE_CREDIT_CARD_INIT =
  'data/creditCards/DEACTIVATE_CREDIT_CARD_INIT'
export const DEACTIVATE_CREDIT_CARD_SUCCESS =
  'data/creditCards/DEACTIVATE_CREDIT_CARD_SUCCESS'
export const DEACTIVATE_CREDIT_CARD_FAILURE =
  'data/creditCards/DEACTIVATE_CREDIT_CARD_FAILURE'

export type DeactivateCreditCardSuccessPayload = {
  creditCard: CreditCardListModel
}
export type DeactivateCreditCardFailurePayload = BackendErrorPayload<string>

export interface DeactivateCreditCardInitAction {
  type: typeof DEACTIVATE_CREDIT_CARD_INIT
}
export interface DeactivateCreditCardSuccessAction {
  type: typeof DEACTIVATE_CREDIT_CARD_SUCCESS
  payload: DeactivateCreditCardSuccessPayload
}
export interface DeactivateCreditCardFailureAction {
  type: typeof DEACTIVATE_CREDIT_CARD_FAILURE
  payload: DeactivateCreditCardFailurePayload
}

export type DeactivateCreditCardActions =
  | DeactivateCreditCardInitAction
  | DeactivateCreditCardSuccessAction
  | DeactivateCreditCardFailureAction

const createDeactivateCreditCardInitAction =
  (): DeactivateCreditCardInitAction => ({
    type: DEACTIVATE_CREDIT_CARD_INIT,
  })
const createDeactivateCreditCardSuccessAction = (
  payload: DeactivateCreditCardSuccessPayload,
): DeactivateCreditCardSuccessAction => ({
  type: DEACTIVATE_CREDIT_CARD_SUCCESS,
  payload,
})
const createDeactivateCreditCardFailureAction = (
  payload: DeactivateCreditCardFailurePayload,
): DeactivateCreditCardFailureAction => ({
  type: DEACTIVATE_CREDIT_CARD_FAILURE,
  payload,
})

export const deactivateCreditCardAndCloseModal = (
  creditCardId: string,
  creditCard: CreditCardListModel,
): ThunkAction<
  Promise<
    DeactivateCreditCardFailureAction | DeactivateCreditCardSuccessAction
  >,
  RootState,
  void,
  DeactivateCreditCardActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeactivateCreditCardActions>,
    getState: () => RootState,
  ): Promise<
    DeactivateCreditCardFailureAction | DeactivateCreditCardSuccessAction
  > => {
    dispatch(createDeactivateCreditCardInitAction())
    return axios
      .put<DeactivateCreditCardSuccessPayload>(
        `/creditcards/${creditCardId}/deactivate`,
        {},
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(connectedPush(budgetOverviewUrl(creditCardId)))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <DeactivateBudgetToastMessage budget={creditCard} />
              ),
            }),
          )
          return dispatch(
            createDeactivateCreditCardSuccessAction(response.data),
          )
        },
        (err) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <DeactivateBudgetFailureToastMessage budget={creditCard} />
              ),
              failureColorScheme: true,
            }),
          )
          return dispatch(
            createDeactivateCreditCardFailureAction(err.response?.data),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_INIT =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_INIT'
export const GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_SUCCESS =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_SUCCESS'
export const GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_FAILURE =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_FAILURE'

export interface GetCreditCardsStatisticsActiveVcnCountSuccessPayload {
  statistics: Array<{ value: number }>
  creditCardIds: string[]
  statisticKeys: Array<keyof CreditCardStatistics>
}
export interface GetCreditCardsStatisticsActiveVcnCountInitAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_INIT
}
export interface GetCreditCardsStatisticsActiveVcnCountSuccessAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_SUCCESS
  payload: GetCreditCardsStatisticsActiveVcnCountSuccessPayload
}
export interface GetCreditCardsStatisticsActiveVcnCountFailureAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_FAILURE
  payload: unknown
}

const createGetCreditCardsStatisticsActiveVcnCountInitAction =
  (): GetCreditCardsStatisticsActiveVcnCountInitAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_INIT,
  })
const createGetCreditCardsStatisticsActiveVcnCountSuccessAction = (
  payload: GetCreditCardsStatisticsActiveVcnCountSuccessPayload,
): GetCreditCardsStatisticsActiveVcnCountSuccessAction => ({
  type: GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_SUCCESS,
  payload,
})
const createGetCreditCardsStatisticsActiveVcnCountFailureAction = (
  err: unknown,
): GetCreditCardsStatisticsActiveVcnCountFailureAction => ({
  type: GET_CREDIT_CARDS_STATISTICS_ACTIVE_VCN_COUNT_FAILURE,
  payload: err,
})

export type GetCreditCardsStatisticsActiveVcnCountActions =
  | GetCreditCardsStatisticsActiveVcnCountInitAction
  | GetCreditCardsStatisticsActiveVcnCountSuccessAction
  | GetCreditCardsStatisticsActiveVcnCountFailureAction

export const getCreditCardsStatisticsActiveVcnCount = (
  creditCardIds: string[],
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCreditCardsStatisticsActiveVcnCountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsStatisticsActiveVcnCountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetCreditCardsStatisticsActiveVcnCountInitAction())

    return axios
      .post<GetCreditCardsStatisticsActiveVcnCountSuccessPayload>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: StatisticModelType.VirtualCard,
            query: {
              status: VirtualCardStatus.Active,
              creditCardId: ccId,
              all: true,
            },
          })),
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetCreditCardsStatisticsActiveVcnCountSuccessAction({
              ...response.data,
              creditCardIds: creditCardIds,
              statisticKeys: ['activeVcnCount'],
            }),
          )
        },
        (err) => {
          dispatch(
            createGetCreditCardsStatisticsActiveVcnCountFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_INIT =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_INIT'
export const GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_SUCCESS =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_SUCCESS'
export const GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_FAILURE =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_FAILURE'

export interface GetCreditCardsStatisticsVcnCountSuccessPayload {
  statistics: Array<{ value: number }>
  creditCardIds: string[]
  statisticKeys: Array<keyof CreditCardStatistics>
}
export interface GetCreditCardsStatisticsVcnCountInitAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_INIT
}
export interface GetCreditCardsStatisticsVcnCountSuccessAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_SUCCESS
  payload: GetCreditCardsStatisticsVcnCountSuccessPayload
}
export interface GetCreditCardsStatisticsVcnCountFailureAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_FAILURE
  payload: unknown
}

const createGetCreditCardsStatisticsVcnCountInitAction =
  (): GetCreditCardsStatisticsVcnCountInitAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_INIT,
  })
const createGetCreditCardsStatisticsVcnCountSuccessAction = (
  payload: GetCreditCardsStatisticsVcnCountSuccessPayload,
): GetCreditCardsStatisticsVcnCountSuccessAction => ({
  type: GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_SUCCESS,
  payload,
})
const createGetCreditCardsStatisticsVcnCountFailureAction = (
  err: unknown,
): GetCreditCardsStatisticsVcnCountFailureAction => ({
  type: GET_CREDIT_CARDS_STATISTICS_VCN_COUNT_FAILURE,
  payload: err,
})

export type GetCreditCardsStatisticsVcnCountActions =
  | GetCreditCardsStatisticsVcnCountInitAction
  | GetCreditCardsStatisticsVcnCountSuccessAction
  | GetCreditCardsStatisticsVcnCountFailureAction

export const getCreditCardsStatisticsVcnCount = (
  creditCardIds: string[],
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCreditCardsStatisticsVcnCountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsStatisticsVcnCountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetCreditCardsStatisticsVcnCountInitAction())

    return axios
      .post<GetCreditCardsStatisticsVcnCountSuccessPayload>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: StatisticModelType.VirtualCard,
            query: {
              creditCardId: ccId,
              all: true,
            },
          })),
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetCreditCardsStatisticsVcnCountSuccessAction({
              ...response.data,
              creditCardIds: creditCardIds,
              statisticKeys: ['vcnCount'],
            }),
          )
        },
        (err) => {
          dispatch(
            createGetCreditCardsStatisticsVcnCountFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_INIT =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_INIT'
export const GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_SUCCESS =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_SUCCESS'
export const GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_FAILURE =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_FAILURE'

export interface GetCreditCardsStatisticsActiveBudgetCountSuccessPayload {
  statistics: Array<{ value: number }>
  creditCardIds: string[]
  statisticKeys: Array<keyof CreditCardStatistics>
}
export interface GetCreditCardsStatisticsActiveBudgetCountInitAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_INIT
}
export interface GetCreditCardsStatisticsActiveBudgetCountSuccessAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_SUCCESS
  payload: GetCreditCardsStatisticsActiveBudgetCountSuccessPayload
}
export interface GetCreditCardsStatisticsActiveBudgetCountFailureAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_FAILURE
  payload: unknown
}

const createGetCreditCardsStatisticsActiveBudgetCountInitAction =
  (): GetCreditCardsStatisticsActiveBudgetCountInitAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_INIT,
  })
const createGetCreditCardsStatisticsActiveBudgetCountSuccessAction = (
  payload: GetCreditCardsStatisticsActiveBudgetCountSuccessPayload,
): GetCreditCardsStatisticsActiveBudgetCountSuccessAction => ({
  type: GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_SUCCESS,
  payload,
})
const createGetCreditCardsStatisticsActiveBudgetCountFailureAction = (
  err: unknown,
): GetCreditCardsStatisticsActiveBudgetCountFailureAction => ({
  type: GET_CREDIT_CARDS_STATISTICS_ACTIVE_BUDGET_COUNT_FAILURE,
  payload: err,
})

export type GetCreditCardsStatisticsActiveBudgetCountActions =
  | GetCreditCardsStatisticsActiveBudgetCountInitAction
  | GetCreditCardsStatisticsActiveBudgetCountSuccessAction
  | GetCreditCardsStatisticsActiveBudgetCountFailureAction

export const getCreditCardsStatisticsActiveBudgetCount = (
  creditCardIds: string[],
  cacheTimestamp: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCreditCardsStatisticsActiveBudgetCountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsStatisticsActiveBudgetCountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetCreditCardsStatisticsActiveBudgetCountInitAction())

    return axios
      .post<GetCreditCardsStatisticsActiveBudgetCountSuccessPayload>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: StatisticModelType.CreditCard,
            query: {
              status: CreditCardStatus.Active,
              type: CreditCardType.Delegate,
              parentCreditCard: ccId,
              cacheTimestamp: cacheTimestamp,
              all: true,
            },
          })),
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetCreditCardsStatisticsActiveBudgetCountSuccessAction({
              ...response.data,
              creditCardIds: creditCardIds,
              statisticKeys: ['activeBudgetCount'],
            }),
          )
        },
        (err) => {
          dispatch(
            createGetCreditCardsStatisticsActiveBudgetCountFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_INIT =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_INIT'
export const GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_SUCCESS =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_SUCCESS'
export const GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_FAILURE =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_FAILURE'

export interface GetCreditCardsStatisticsBudgetCountSuccessPayload {
  statistics: Array<{ value: number }>
  creditCardIds: string[]
  statisticKeys: Array<keyof CreditCardStatistics>
}
export interface GetCreditCardsStatisticsBudgetCountInitAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_INIT
}
export interface GetCreditCardsStatisticsBudgetCountSuccessAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_SUCCESS
  payload: GetCreditCardsStatisticsBudgetCountSuccessPayload
}
export interface GetCreditCardsStatisticsBudgetCountFailureAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_FAILURE
  payload: unknown
}

const createGetCreditCardsStatisticsBudgetCountInitAction =
  (): GetCreditCardsStatisticsBudgetCountInitAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_INIT,
  })
const createGetCreditCardsStatisticsBudgetCountSuccessAction = (
  payload: GetCreditCardsStatisticsBudgetCountSuccessPayload,
): GetCreditCardsStatisticsBudgetCountSuccessAction => ({
  type: GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_SUCCESS,
  payload,
})
const createGetCreditCardsStatisticsBudgetCountFailureAction = (
  err: unknown,
): GetCreditCardsStatisticsBudgetCountFailureAction => ({
  type: GET_CREDIT_CARDS_STATISTICS_BUDGET_COUNT_FAILURE,
  payload: err,
})

export type GetCreditCardsStatisticsBudgetCountActions =
  | GetCreditCardsStatisticsBudgetCountInitAction
  | GetCreditCardsStatisticsBudgetCountSuccessAction
  | GetCreditCardsStatisticsBudgetCountFailureAction

export const getCreditCardsStatisticsBudgetCount = (
  creditCardIds: string[],
  cacheTimestamp: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCreditCardsStatisticsBudgetCountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsStatisticsBudgetCountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetCreditCardsStatisticsBudgetCountInitAction())

    return axios
      .post<GetCreditCardsStatisticsBudgetCountSuccessPayload>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: StatisticModelType.CreditCard,
            query: {
              type: CreditCardType.Delegate,
              parentCreditCard: ccId,
              cacheTimestamp: cacheTimestamp,
              all: true,
            },
          })),
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetCreditCardsStatisticsBudgetCountSuccessAction({
              ...response.data,
              creditCardIds: creditCardIds,
              statisticKeys: ['budgetCount'],
            }),
          )
        },
        (err) => {
          dispatch(
            createGetCreditCardsStatisticsBudgetCountFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_INIT = 'data/creditCards/GET_CREDIT_CARDS_INIT'
export const GET_CREDIT_CARDS_SUCCESS =
  'data/creditCards/GET_CREDIT_CARDS_SUCCESS'
export const GET_CREDIT_CARDS_FAILURE =
  'data/creditCards/GET_CREDIT_CARDS_FAILURE'

export interface GetCreditCardsInitPayload {
  queryParameters: GetCreditCardsRequest
}

export interface GetCreditCardsSuccessPayload {
  creditCards: CreditCardListResponse[]
  pagination: PaginationResponse
  queryParameters: GetCreditCardsRequest
}

export interface GetCreditCardsFailurePayload {
  queryParameters: GetCreditCardsRequest
}

export interface GetCreditCardsInitAction {
  type: typeof GET_CREDIT_CARDS_INIT
  payload: GetCreditCardsInitPayload
}
export interface GetCreditCardsSuccessAction {
  type: typeof GET_CREDIT_CARDS_SUCCESS
  payload: GetCreditCardsSuccessPayload
}
export interface GetCreditCardsFailureAction {
  type: typeof GET_CREDIT_CARDS_FAILURE
  payload: GetCreditCardsFailurePayload
}

export type GetCreditCardsActions =
  | GetCreditCardsInitAction
  | GetCreditCardsSuccessAction
  | GetCreditCardsFailureAction

const createGetCreditCardsInitAction = (
  payload: GetCreditCardsInitPayload,
): GetCreditCardsInitAction => ({
  type: GET_CREDIT_CARDS_INIT,
  payload,
})
const createGetCreditCardsSuccessAction = (
  payload: GetCreditCardsSuccessPayload,
): GetCreditCardsSuccessAction => ({
  type: GET_CREDIT_CARDS_SUCCESS,
  payload,
})
const createGetCreditCardsFailureAction = (
  payload: GetCreditCardsFailurePayload,
): GetCreditCardsFailureAction => ({
  type: GET_CREDIT_CARDS_FAILURE,
  payload,
})

export const getCreditCards = (
  queryParameters: GetCreditCardsRequest,
): ThunkAction<
  Promise<void | GetCreditCardsFailureAction>,
  RootState,
  void,
  GetCreditCardsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetCreditCardsActions>,
    getState: () => RootState,
  ): Promise<void | GetCreditCardsFailureAction> => {
    dispatch(createGetCreditCardsInitAction({ queryParameters }))
    return axios
      .get<GetCreditCardsSuccessPayload>(
        `/creditcards?${queryString.stringify(queryParameters)}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const creditCardIds: string[] = response.data.creditCards.map(
            (cc) => cc.id,
          )
          dispatch(
            getCreditCardsStatisticsActiveBudgetCount(
              creditCardIds,
              cacheTimestamp,
            ),
          )
          dispatch(
            getCreditCardsStatisticsBudgetCount(creditCardIds, cacheTimestamp),
          )
          dispatch(getCreditCardsStatisticsActiveVcnCount(creditCardIds))
          dispatch(getCreditCardsStatisticsVcnCount(creditCardIds))

          const amexIssuerCodes = [
            IssuerCodes.AmexCel,
            IssuerCodes.Amex,
            IssuerCodes.AmexTest,
          ]
          // map through credit cards and call getCreditCardProperties action on each when the issuer is AMEXCEL
          // NOTE: this sucks and will need re-architecture on the API side to make this more scalable
          response.data.creditCards.map((cc) => {
            if (amexIssuerCodes.includes(cc.issuer as IssuerCodes)) {
              dispatch(getCreditCardProperties(cc.id))
            }
          })

          dispatch(
            createGetCreditCardsSuccessAction({
              ...response.data,
              queryParameters,
            }),
          )
          if (queryParameters.all) {
            const { numberOfPages, page } = response.data.pagination
            if (page < numberOfPages - 1) {
              dispatch(
                getCreditCards({
                  ...queryParameters,
                  page: page + 1,
                }),
              )
            }
          }
        },
        (err) => {
          return dispatch(
            createGetCreditCardsFailureAction({
              ...err.response.data,
              queryParameters,
            }),
          )
        },
      )
  }
}

export const GET_ALL_CREDIT_CARDS_SUCCESS =
  'data/creditCards/GET_ALL_CREDIT_CARDS_SUCCESS'

export interface GetAllCreditCardsSuccessPayload {
  creditCards: CreditCardListResponse[]
  pagination: PaginationResponse
}
export interface GetAllCreditCardsSuccessAction {
  type: typeof GET_ALL_CREDIT_CARDS_SUCCESS
  payload: GetAllCreditCardsSuccessPayload
}

export type GetAllCreditCardsActions = GetAllCreditCardsSuccessAction

const createGetAllCreditCardsSuccessAction = (
  payload: GetAllCreditCardsSuccessPayload,
): GetAllCreditCardsSuccessAction => ({
  type: GET_ALL_CREDIT_CARDS_SUCCESS,
  payload,
})

export const getAllCreditCards = (
  queryParameters: GetCreditCardsRequest,
): ThunkAction<void, RootState, void, GetAllCreditCardsActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetAllCreditCardsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    return axios
      .get<GetAllCreditCardsSuccessPayload>(
        `/creditcards?${queryString.stringify(queryParameters)}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then((response) => {
        dispatch(createGetAllCreditCardsSuccessAction({ ...response.data }))

        if (queryParameters.all) {
          const { numberOfPages, page } = response.data.pagination
          if (page < numberOfPages - 1) {
            dispatch(
              getAllCreditCards({
                ...queryParameters,
                page: page + 1,
              }),
            )
          }
        }
      })
  }
}

export const POST_CREDIT_CARD_VERIFY_INIT =
  'data/creditCards/POST_CREDIT_CARD_VERIFY_INIT'
export const POST_CREDIT_CARD_VERIFY_SUCCESS =
  'data/creditCards/POST_CREDIT_CARD_VERIFY_SUCCESS'
export const POST_CREDIT_CARD_VERIFY_FAILURE =
  'data/creditCards/POST_CREDIT_CARD_VERIFY_FAILURE'

export type PostCreditCardVerifySuccessPayload = {
  creditCard: CreditCardListResponse
}
export type PostCreditCardVerifyFailurePayload = BackendErrorPayload<string>

export interface PostCreditCardVerifyInitAction {
  type: typeof POST_CREDIT_CARD_VERIFY_INIT
}
export interface PostCreditCardVerifySuccessAction {
  type: typeof POST_CREDIT_CARD_VERIFY_SUCCESS
  payload: PostCreditCardVerifySuccessPayload
}
export interface PostCreditCardVerifyFailureAction {
  type: typeof POST_CREDIT_CARD_VERIFY_FAILURE
  payload: PostCreditCardVerifyFailurePayload
}

export type PostCreditCardVerifyActions =
  | PostCreditCardVerifyInitAction
  | PostCreditCardVerifySuccessAction
  | PostCreditCardVerifyFailureAction

const createPostCreditCardVerifyInitAction =
  (): PostCreditCardVerifyInitAction => ({
    type: POST_CREDIT_CARD_VERIFY_INIT,
  })
const createPostCreditCardVerifySuccessAction = (
  payload: PostCreditCardVerifySuccessPayload,
): PostCreditCardVerifySuccessAction => ({
  type: POST_CREDIT_CARD_VERIFY_SUCCESS,
  payload,
})
const createPostCreditCardVerifyFailureAction = (
  payload: PostCreditCardVerifyFailurePayload,
): PostCreditCardVerifyFailureAction => ({
  type: POST_CREDIT_CARD_VERIFY_FAILURE,
  payload,
})

export const postCreditCardVerifyThenOpenToast = (
  creditCardId: string,
  body: { code: string },
): ThunkAction<
  Promise<
    PostCreditCardVerifyFailureAction | PostCreditCardVerifySuccessAction
  >,
  RootState,
  void,
  PostCreditCardVerifyActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostCreditCardVerifyActions>,
    getState: () => RootState,
  ): Promise<
    PostCreditCardVerifyFailureAction | PostCreditCardVerifySuccessAction
  > => {
    const dispatchStatistics = selectStatisticsDispatcherFromState(getState())
    dispatch(createPostCreditCardVerifyInitAction())
    return axios
      .post(
        `/creditcards/${creditCardId}/verify`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          if (response.data.creditCard.status !== CreditCardStatus.Blocked) {
            dispatchStatistics([
              appStatisticsKeys.CreditCardsCanIssueVirtualCardsCountStatistics,
              appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
            ], cacheTimestamp)
            queryClient.invalidateQueries({
              queryKey: creditCardsQueryKeys.detail(creditCardId),
            })
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <CreateCreditCardVerificationSuccessMessage />,
              }),
            )
          }
          return dispatch(
            createPostCreditCardVerifySuccessAction(response.data),
          )
        },
        (err) => {
          return dispatch(
            createPostCreditCardVerifyFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_CREDIT_CARD_START_VERIFICATION_INIT =
  'data/creditCards/POST_CREDIT_CARD_START_VERIFICATION_INIT'
export const POST_CREDIT_CARD_START_VERIFICATION_SUCCESS =
  'data/creditCards/POST_CREDIT_CARD_START_VERIFICATION_SUCCESS'
export const POST_CREDIT_CARD_START_VERIFICATION_FAILURE =
  'data/creditCards/POST_CREDIT_CARD_START_VERIFICATION_FAILURE'

export type PostCreditCardStartVerificationSuccessPayload = {
  creditCard: CreditCardListResponse
}
export type PostCreditCardStartVerificationFailurePayload =
  BackendErrorPayload<string>

export interface PostCreditCardStartVerificationInitAction {
  type: typeof POST_CREDIT_CARD_START_VERIFICATION_INIT
}
export interface PostCreditCardStartVerificationSuccessAction {
  type: typeof POST_CREDIT_CARD_START_VERIFICATION_SUCCESS
  payload: PostCreditCardStartVerificationSuccessPayload
}
export interface PostCreditCardStartVerificationFailureAction {
  type: typeof POST_CREDIT_CARD_START_VERIFICATION_FAILURE
  payload: PostCreditCardStartVerificationFailurePayload
}

export type PostCreditCardStartVerificationActions =
  | PostCreditCardStartVerificationInitAction
  | PostCreditCardStartVerificationSuccessAction
  | PostCreditCardStartVerificationFailureAction

const createPostCreditCardStartVerificationInitAction =
  (): PostCreditCardStartVerificationInitAction => ({
    type: POST_CREDIT_CARD_START_VERIFICATION_INIT,
  })
const createPostCreditCardStartVerificationSuccessAction = (
  payload: PostCreditCardStartVerificationSuccessPayload,
): PostCreditCardStartVerificationSuccessAction => ({
  type: POST_CREDIT_CARD_START_VERIFICATION_SUCCESS,
  payload,
})
const createPostCreditCardStartVerificationFailureAction = (
  payload: PostCreditCardStartVerificationFailurePayload,
): PostCreditCardStartVerificationFailureAction => ({
  type: POST_CREDIT_CARD_START_VERIFICATION_FAILURE,
  payload,
})

export const postCreditCardStartVerificationThenOpenToast = (
  creditCardId: string,
): ThunkAction<
  Promise<
    | PostCreditCardStartVerificationFailureAction
    | PostCreditCardStartVerificationSuccessAction
  >,
  RootState,
  void,
  PostCreditCardStartVerificationActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCreditCardStartVerificationActions
    >,
    getState: () => RootState,
  ): Promise<
    | PostCreditCardStartVerificationFailureAction
    | PostCreditCardStartVerificationSuccessAction
  > => {
    dispatch(createPostCreditCardStartVerificationInitAction())
    return axios
      .post(
        `/creditcards/${creditCardId}/startverification`,
        undefined,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <NewVerificationCodeSentToastMessage />,
            }),
          )
          return dispatch(
            createPostCreditCardStartVerificationSuccessAction(response.data),
          )
        },
        (err) => {
          return dispatch(
            createPostCreditCardStartVerificationFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const REPLACE_CREDIT_CARD_INIT =
  'data/creditCards/REPLACE_CREDIT_CARD_INIT'
export const REPLACE_CREDIT_CARD_SUCCESS =
  'data/creditCards/REPLACE_CREDIT_CARD_SUCCESS'
export const REPLACE_CREDIT_CARD_FAILURE =
  'data/creditCards/REPLACE_CREDIT_CARD_FAILURE'

export type ReplaceCreditCardSuccessPayload = {
  creditCard: CreditCardListResponse
}
export type ReplaceCreditCardFailurePayload = BackendErrorPayload<string>

export interface ReplaceCreditCardInitAction {
  type: typeof REPLACE_CREDIT_CARD_INIT
}
export interface ReplaceCreditCardSuccessAction {
  type: typeof REPLACE_CREDIT_CARD_SUCCESS
  payload: ReplaceCreditCardSuccessPayload
}
export interface ReplaceCreditCardFailureAction {
  type: typeof REPLACE_CREDIT_CARD_FAILURE
  payload: ReplaceCreditCardFailurePayload
}

export type ReplaceCreditCardActions =
  | ReplaceCreditCardInitAction
  | ReplaceCreditCardSuccessAction
  | ReplaceCreditCardFailureAction

const createReplaceCreditCardInitAction = (): ReplaceCreditCardInitAction => ({
  type: REPLACE_CREDIT_CARD_INIT,
})
const createReplaceCreditCardSuccessAction = (
  payload: ReplaceCreditCardSuccessPayload,
): ReplaceCreditCardSuccessAction => ({
  type: REPLACE_CREDIT_CARD_SUCCESS,
  payload,
})
const createReplaceCreditCardFailureAction = (
  payload: ReplaceCreditCardFailurePayload,
): ReplaceCreditCardFailureAction => ({
  type: REPLACE_CREDIT_CARD_FAILURE,
  payload,
})

export const replaceCreditCard = (
  creditCardId: string,
  body: Partial<PatchAccountRequest>,
): ThunkAction<
  Promise<ReplaceCreditCardFailureAction | ReplaceCreditCardSuccessAction>,
  RootState,
  void,
  ReplaceCreditCardActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, ReplaceCreditCardActions>,
    getState: () => RootState,
  ): Promise<
    ReplaceCreditCardFailureAction | ReplaceCreditCardSuccessAction
  > => {
    dispatch(createReplaceCreditCardInitAction())
    return axios
      .patch(
        `/creditcards/${creditCardId}/replace`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          return dispatch(createReplaceCreditCardSuccessAction(response.data))
        },
        (err) => {
          return dispatch(
            createReplaceCreditCardFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_INIT =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_INIT'
export const GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS'
export const GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_FAILURE =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_FAILURE'

export interface GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessPayload {
  statistics: Array<{ value: number }>
  creditCardIds: string[]
  statisticKeys: Array<keyof CreditCardStatistics>
}
export interface GetCreditCardsStatisticsClearedTransactionDebitAmountInitAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_INIT
}
export interface GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS
  payload: GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessPayload
}
export interface GetCreditCardsStatisticsClearedTransactionDebitAmountFailureAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_FAILURE
  payload: unknown
}

const createGetCreditCardsStatisticsClearedTransactionDebitAmountInitAction =
  (): GetCreditCardsStatisticsClearedTransactionDebitAmountInitAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_INIT,
  })
const createGetCreditCardsStatisticsClearedTransactionDebitAmountSuccessAction =
  (
    payload: GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessPayload,
  ): GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_SUCCESS,
    payload,
  })
const createGetCreditCardsStatisticsClearedTransactionDebitAmountFailureAction =
  (
    err: unknown,
  ): GetCreditCardsStatisticsClearedTransactionDebitAmountFailureAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_DEBIT_AMOUNT_FAILURE,
    payload: err,
  })

export type GetCreditCardsStatisticsClearedTransactionDebitAmountActions =
  | GetCreditCardsStatisticsClearedTransactionDebitAmountInitAction
  | GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessAction
  | GetCreditCardsStatisticsClearedTransactionDebitAmountFailureAction

export const getCreditCardsStatisticsClearedTransactionDebitAmount = (
  creditCardIds: string[],
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCreditCardsStatisticsClearedTransactionDebitAmountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsStatisticsClearedTransactionDebitAmountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(
      createGetCreditCardsStatisticsClearedTransactionDebitAmountInitAction(),
    )

    return axios
      .post<GetCreditCardsStatisticsClearedTransactionDebitAmountSuccessPayload>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Sum,
            type: StatisticModelType.Transaction,
            query: {
              creditCardId: [ccId],
              type: TransactionType.Debit,
              field:
                TransactionStatisticQueryFieldType.clearingBillingAmountCents,
              status: [TransactionStatus.Cleared],
            },
          })),
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetCreditCardsStatisticsClearedTransactionDebitAmountSuccessAction(
              {
                ...response.data,
                creditCardIds: creditCardIds,
                statisticKeys: ['clearedTransactionDebitAmount'],
              },
            ),
          )
        },
        (err) => {
          dispatch(
            createGetCreditCardsStatisticsClearedTransactionDebitAmountFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_INIT =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_INIT'
export const GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS'
export const GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_FAILURE =
  'data/creditCards/GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_FAILURE'

export interface GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessPayload {
  statistics: Array<{ value: number }>
  creditCardIds: string[]
  statisticKeys: Array<keyof CreditCardStatistics>
}
export interface GetCreditCardsStatisticsClearedTransactionCreditAmountInitAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_INIT
}
export interface GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS
  payload: GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessPayload
}
export interface GetCreditCardsStatisticsClearedTransactionCreditAmountFailureAction {
  type: typeof GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_FAILURE
  payload: unknown
}

const createGetCreditCardsStatisticsClearedTransactionCreditAmountInitAction =
  (): GetCreditCardsStatisticsClearedTransactionCreditAmountInitAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_INIT,
  })
const createGetCreditCardsStatisticsClearedTransactionCreditAmountSuccessAction =
  (
    payload: GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessPayload,
  ): GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_SUCCESS,
    payload,
  })
const createGetCreditCardsStatisticsClearedTransactionCreditAmountFailureAction =
  (
    err: unknown,
  ): GetCreditCardsStatisticsClearedTransactionCreditAmountFailureAction => ({
    type: GET_CREDIT_CARDS_STATISTICS_CLEARED_TRANSACTION_CREDIT_AMOUNT_FAILURE,
    payload: err,
  })

export type GetCreditCardsStatisticsClearedTransactionCreditAmountActions =
  | GetCreditCardsStatisticsClearedTransactionCreditAmountInitAction
  | GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessAction
  | GetCreditCardsStatisticsClearedTransactionCreditAmountFailureAction

export const getCreditCardsStatisticsClearedTransactionCreditAmount = (
  creditCardIds: string[],
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCreditCardsStatisticsClearedTransactionCreditAmountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsStatisticsClearedTransactionCreditAmountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(
      createGetCreditCardsStatisticsClearedTransactionCreditAmountInitAction(),
    )

    return axios
      .post<GetCreditCardsStatisticsClearedTransactionCreditAmountSuccessPayload>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Sum,
            type: StatisticModelType.Transaction,
            query: {
              creditCardId: [ccId],
              type: TransactionType.Credit,
              field:
                TransactionStatisticQueryFieldType.clearingBillingAmountCents,
              status: [TransactionStatus.Cleared],
            },
          })),
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createGetCreditCardsStatisticsClearedTransactionCreditAmountSuccessAction(
              {
                ...response.data,
                creditCardIds: creditCardIds,
                statisticKeys: ['clearedTransactionCreditAmount'],
              },
            ),
          )
        },
        (err) => {
          dispatch(
            createGetCreditCardsStatisticsClearedTransactionCreditAmountFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_ISSUER_FROM_BIN_INIT =
  'data/creditCards/GET_ISSUER_FROM_BIN_INIT'
export const GET_ISSUER_FROM_BIN_SUCCESS =
  'data/creditCards/GET_ISSUER_FROM_BIN_SUCCESS'
export const GET_ISSUER_FROM_BIN_UNSUPPORTED_SUCCESS =
  'data/creditCards/GET_ISSUER_FROM_BIN_UNSUPPORTED_SUCCESS'
export const GET_ISSUER_FROM_BIN_FAILURE =
  'data/creditCards/GET_ISSUER_FROM_BIN_FAILURE'

export interface GetIssuerFromBinSuccessPayload {
  binRange: BinRangeResponse
}

export interface GetIssuerFromBinUnsupportedSuccessPayload {
  binRange: BinRangeResponse
  error: string
}

export type GetIssuerFromBinFailurePayload = BackendErrorPayload<string>

export interface GetIssuerFromBinInitAction {
  type: typeof GET_ISSUER_FROM_BIN_INIT
}
export interface GetIssuerFromBinSuccessAction {
  type: typeof GET_ISSUER_FROM_BIN_SUCCESS
  payload: GetIssuerFromBinSuccessPayload
}
export interface GetIssuerFromBinUnsupportedSuccessAction {
  type: typeof GET_ISSUER_FROM_BIN_UNSUPPORTED_SUCCESS
  payload: GetIssuerFromBinUnsupportedSuccessPayload
}
export interface GetIssuerFromBinFailureAction {
  type: typeof GET_ISSUER_FROM_BIN_FAILURE
  payload: GetIssuerFromBinFailurePayload
}

export type GetIssuerFromBinActions =
  | GetIssuerFromBinInitAction
  | GetIssuerFromBinSuccessAction
  | GetIssuerFromBinUnsupportedSuccessAction
  | GetIssuerFromBinFailureAction

const createGetIssuerFromBinInitAction = (): GetIssuerFromBinInitAction => ({
  type: GET_ISSUER_FROM_BIN_INIT,
})
const createGetIssuerFromBinSuccessAction = (
  payload: GetIssuerFromBinSuccessPayload,
): GetIssuerFromBinSuccessAction => ({
  type: GET_ISSUER_FROM_BIN_SUCCESS,
  payload,
})
const createGetIssuerFromBinUnsupportedSuccessAction = (
  payload: GetIssuerFromBinUnsupportedSuccessPayload,
): GetIssuerFromBinUnsupportedSuccessAction => ({
  type: GET_ISSUER_FROM_BIN_UNSUPPORTED_SUCCESS,
  payload,
})
const createGetIssuerFromBinFailureAction = (
  response: GetIssuerFromBinFailurePayload,
): GetIssuerFromBinFailureAction => ({
  type: GET_ISSUER_FROM_BIN_FAILURE,
  payload: response,
})

export interface GetIssuerFromBinQueryParams {
  bin?: string
}

export const getIssuerFromBin = (
  queryParams: GetIssuerFromBinQueryParams,
  getIssuerIntegrator?: boolean,
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<
  Promise<
    | GetIssuerFromBinFailureAction
    | GetIssuerFromBinSuccessAction
    | GetIssuerFromBinUnsupportedSuccessAction
  >,
  RootState,
  void,
  GetIssuerFromBinActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetIssuerFromBinActions>,
    getState: () => RootState,
  ): Promise<
    | GetIssuerFromBinFailureAction
    | GetIssuerFromBinSuccessAction
    | GetIssuerFromBinUnsupportedSuccessAction
  > => {
    dispatch(createGetIssuerFromBinInitAction())
    return axios
      .get<GetIssuerFromBinSuccessPayload>(
        `/binranges/?${stringify(queryParams)}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          const locale = selectNullSafeLocaleFromState(getState())
          // if not supported, replicate error response
          if (response.data.binRange.supported) {
            if (getIssuerIntegrator && response.data.binRange.issuerId) {
              dispatch(getIssuer(response.data.binRange.issuerId))
            }
            return dispatch(createGetIssuerFromBinSuccessAction(response.data))
          } else {
            analytics?.track(
              'register account - card info - error message unsupported BIN',
              {
                intent: "can't continue to billing info - unsupported BIN",
                interaction: 'button press',
                screen: registerAccountUrl(),
                binRange: `${response.data.binRange.rangeMin}-${response.data.binRange.rangeMax}`,
              },
            )
            return dispatch(
              createGetIssuerFromBinUnsupportedSuccessAction({
                error: translateSingleString(
                  locale,
                  'The issuer of this card number is currently not supported',
                ).toString(),
                binRange: response.data.binRange,
              }),
            )
          }
        },
        (err) => {
          analytics?.track(
            'register account - card info - error message unsupported BIN - no match',
            {
              intent: "can't continue to billing info - unsupported BIN",
              interaction: 'button press',
              screen: registerAccountUrl(),
            },
          )
          return dispatch(
            createGetIssuerFromBinFailureAction(err.response.data),
          )
        },
      )
  }
}

export const CLEAR_BIN_RANGE_INFO = 'app/registerAccount/CLEAR_BIN_RANGE_INFO'

export interface ClearBinRangeInfoAction {
  type: typeof CLEAR_BIN_RANGE_INFO
}

export const createClearBinRangeInfoAction = (): ClearBinRangeInfoAction => ({
  type: CLEAR_BIN_RANGE_INFO,
})

export const GET_CREDIT_CARD_PROPERTIES_INIT =
  'data/creditCards/GET_CREDIT_CARD_PROPERTIES_INIT'
export const GET_CREDIT_CARD_PROPERTIES_SUCCESS =
  'data/creditCards/GET_CREDIT_CARD_PROPERTIES_SUCCESS'
export const GET_CREDIT_CARD_PROPERTIES_FAILURE =
  'data/creditCards/GET_CREDIT_CARD_PROPERTIES_FAILURE'

export type GetCreditCardPropertiesSuccessPayload = {
  cardProperties: CardProperties
}

export type GetCreditCardPropertiesFailurePayload = BackendErrorPayload<string>

export interface GetCreditCardPropertiesInitAction {
  type: typeof GET_CREDIT_CARD_PROPERTIES_INIT
}
export interface GetCreditCardPropertiesSuccessAction {
  type: typeof GET_CREDIT_CARD_PROPERTIES_SUCCESS
  payload: GetCreditCardPropertiesSuccessPayload
}
export interface GetCreditCardPropertiesFailureAction {
  type: typeof GET_CREDIT_CARD_PROPERTIES_FAILURE
  payload: GetCreditCardPropertiesFailurePayload
}

const createGetCreditCardPropertiesInitAction =
  (): GetCreditCardPropertiesInitAction => ({
    type: GET_CREDIT_CARD_PROPERTIES_INIT,
  })
const createGetCreditCardPropertiesSuccessAction = (
  payload: GetCreditCardPropertiesSuccessPayload,
): GetCreditCardPropertiesSuccessAction => ({
  type: GET_CREDIT_CARD_PROPERTIES_SUCCESS,
  payload,
})
const createGetCreditCardPropertiesFailureAction = (
  err: GetCreditCardPropertiesFailurePayload,
): GetCreditCardPropertiesFailureAction => ({
  type: GET_CREDIT_CARD_PROPERTIES_FAILURE,
  payload: err,
})

export type GetCreditCardPropertiesActions =
  | GetCreditCardPropertiesInitAction
  | GetCreditCardPropertiesSuccessAction
  | GetCreditCardPropertiesFailureAction

export const getCreditCardProperties = (
  creditCardId: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCreditCardPropertiesActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetCreditCardPropertiesActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetCreditCardPropertiesInitAction())

    return axios
      .get<GetCreditCardPropertiesSuccessPayload>(
        `/creditcards/${creditCardId}/properties`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetCreditCardPropertiesSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createGetCreditCardPropertiesFailureAction(err.response.data),
          )
        },
      )
  }
}

export const PUT_CLOSE_ACCOUNT_INIT = 'data/virtualCards/PUT_CLOSE_ACCOUNT_INIT'
export const PUT_CLOSE_ACCOUNT_SUCCESS =
  'data/virtualCards/PUT_CLOSE_ACCOUNT_SUCCESS'
export const PUT_CLOSE_ACCOUNT_FAILURE =
  'data/virtualCards/PUT_CLOSE_ACCOUNT_FAILURE'

export type PutCloseAccountSuccessPayload = {
  payload: string
}
export type PutCloseAccountFailurePayload = BackendErrorPayload<string>

export interface PutCloseAccountInitAction {
  type: typeof PUT_CLOSE_ACCOUNT_INIT
}
export interface PutCloseAccountSuccessAction {
  type: typeof PUT_CLOSE_ACCOUNT_SUCCESS
  payload: PutCloseAccountSuccessPayload
}
export interface PutCloseAccountFailureAction {
  type: typeof PUT_CLOSE_ACCOUNT_FAILURE
  payload: PutCloseAccountFailurePayload
}

export type PutCloseAccountActions =
  | PutCloseAccountInitAction
  | PutCloseAccountSuccessAction
  | PutCloseAccountFailureAction

const createPutCloseAccountInitAction = (): PutCloseAccountInitAction => ({
  type: PUT_CLOSE_ACCOUNT_INIT,
})
const createPutCloseAccountSuccessAction = (
  payload: PutCloseAccountSuccessPayload,
): PutCloseAccountSuccessAction => ({
  type: PUT_CLOSE_ACCOUNT_SUCCESS,
  payload,
})
const createPutCloseAccountFailureAction = (
  response: PutCloseAccountFailurePayload,
): PutCloseAccountFailureAction => ({
  type: PUT_CLOSE_ACCOUNT_FAILURE,
  payload: response,
})

export const putCloseAccountAndCloseModal = (
  id: string,
  displayName: string,
): ThunkAction<Promise<void>, RootState, void, PutCloseAccountActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutCloseAccountActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutCloseAccountInitAction())
    return axios
      .put<PutCloseAccountSuccessPayload>(
        `/creditcards/${id}/close`,
        undefined,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutCloseAccountSuccessAction(response.data))
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <CloseAccountToastMessage displayName={displayName} id={id} />
              ),
            }),
          )
        },
        (err) => {
          dispatch(createPutCloseAccountFailureAction(err.response.data))
        },
      )
  }
}
